import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, PlayCircle, Sparkles, RotateCcw, ShieldCheck } from "lucide-react";
import { MaskedLines } from "@/components/Reveal";
import InstagramStories from "@/components/InstagramStories";

const HeroCopy = () => (
        <div className="lg:col-span-7 relative z-10">
          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="eyebrow faro-pulse-dot"
            data-testid="hero-eyebrow"
          >
            Autoescuela en Vigo para coche, moto, camión, autobús y CAP
          </motion.h1>

          <motion.a
            href="/recuperacion-de-puntos-vigo"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.12 }}
            className="mt-4 group inline-flex flex-col gap-2 rounded-2xl border border-[rgba(30,42,143,0.15)] bg-[rgba(30,42,143,0.045)] px-4 py-3 sm:px-5 sm:py-4 max-w-2xl hover:bg-[rgba(30,42,143,0.075)] hover:border-[rgba(30,42,143,0.35)] transition-all"
            data-testid="hero-puntos-promo"
          >
            <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-black">
              <span
                className="inline-flex items-center gap-1.5 rounded-full bg-[var(--faro-blue-deep)] text-white px-2.5 py-1"
                data-testid="hero-promo-badge-dgt"
              >
                <RotateCcw size={12} /> Centro autorizado DGT en Vigo
              </span>
              <span
                className="inline-flex items-center gap-1.5 rounded-full bg-[var(--faro-yellow)] text-[var(--faro-blue-deep)] px-2.5 py-1"
                data-testid="hero-promo-badge-seguridad"
              >
                <ShieldCheck size={12} /> Seguridad vial
              </span>
            </div>
            <p
              className="font-display font-black tracking-tight text-[var(--faro-text)] text-lg sm:text-xl leading-tight"
              data-testid="hero-promo-title"
            >
              Recuperación de puntos y del permiso · Centro autorizado DGT · Plaza América
            </p>
            <p
              className="text-sm text-[var(--faro-text-soft)] inline-flex items-center gap-1"
              data-testid="hero-promo-subtitle"
            >
              Fechas, horarios, precios y reserva online.
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
            </p>
          </motion.a>

          <div
            className="font-display font-black tracking-tighter text-[var(--faro-text)] mt-5 leading-[0.92] text-5xl sm:text-6xl lg:text-7xl"
            data-testid="hero-title"
          >
            <MaskedLines
              lines={["Empieza tu", "carnet con", "quien conduce", "Vigo desde 1980."]}
              delay={0}
              stagger={0.02}
              lineClassName="will-change-transform"
            />
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.7 }}
            className="mt-4 sm:mt-6 text-base sm:text-lg text-[var(--faro-text-soft)] leading-relaxed max-w-2xl"
            data-testid="hero-subtitle"
          >
            5 centros · 15+ coches · 6 motos · pistas propias · camión, autobús y cuadriciclo.
            Una autoescuela con una de las flotas más completas del sur de Galicia.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.8 }}
            className="mt-5 sm:mt-8 flex flex-wrap items-center gap-3"
          >
            <a href="#contacto" className="btn-pill btn-primary" data-testid="hero-cta-primary">
              Empieza tu carnet <ArrowRight size={18} />
            </a>
            <a href="#test" className="btn-pill btn-ghost" data-testid="hero-cta-secondary">
              <PlayCircle size={18} /> Hazte un test de prueba
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.95 }}
            className="mt-6 sm:mt-10 hidden sm:flex flex-wrap items-center gap-6 text-sm text-[var(--faro-text-soft)]"
          >
            <span className="inline-flex items-center gap-2">
              <Sparkles size={16} className="text-[var(--faro-red)]" /> Pistas propias para examen DGT
            </span>
            <span className="inline-flex items-center gap-2">
              <Sparkles size={16} className="text-[var(--faro-red)]" /> Vehículo adaptado a diversidad funcional
            </span>
          </motion.div>
        </div>
);

export default function Hero() {
  return (
    <section className="relative overflow-hidden pt-10 sm:pt-16 pb-20">
      <div className="absolute inset-0 faro-grid-bg opacity-60 pointer-events-none" aria-hidden="true" />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center relative">
        <HeroCopy />

        <div className="lg:col-span-5 relative">
          <div
            className="relative aspect-[16/10] sm:aspect-[4/5] max-h-[380px] sm:max-h-none rounded-[1.5rem] sm:rounded-[2rem] overflow-hidden border border-[rgba(30,42,143,0.15)] shadow-2xl shadow-[rgba(30,42,143,0.18)] bg-[var(--faro-blue-deep)]"
            data-testid="hero-image"
          >
            <InstagramStories />
          </div>
        </div>
      </div>
    </section>
  );
}
